import { useTranslate } from "@refinedev/core";
import {
  Button,
  Card,
  Input,
  Typography,
  Form,
  Tabs,
  Space,
  Flex,
  ConfigProvider,
  Dropdown,
  MenuProps,
} from "antd";
import {
  ArrowUpOutlined,
  CheckOutlined,
  CheckCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";
import React, { CSSProperties, useEffect, useState } from "react";
import type { TScript } from "pages/media/types";
import { useAntTheme } from "hooks/useAntTheme";
import {
  MagicWandIcon,
  StackMinusIcon,
  StackPlusIcon,
  TextTIcon,
} from "@phosphor-icons/react";
import { countScriptWords } from "pages/media/helpers/scriptAnchors";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics";
import { Label } from "./Label";
import { ScriptWithHighlights } from "./ScriptWithHighlights";
import { PlayScript } from "components/PlayScript/";
import { splitTextWithHighlightTags } from "./helpers";
import { useTextToSpeech } from "@/hooks/useTextToSpeech";

type ScriptProps = {
  changeScript: (id: number, keyInsight: Partial<TScript>) => void;
  improveScript: (
    sectionId: string,
    script: TScript,
    message: string,
    callback: (scripts: TScript[]) => void
  ) => void;
  script: TScript;
  index: number;
  sectionId: string;
  editable: boolean;
  showPrompt?: boolean;
  showSelect?: boolean;
  isBusy?: boolean;
};

export const Script: React.FC<ScriptProps> = ({
  changeScript,
  improveScript,
  sectionId,
  script,
  index,
  editable,
  showPrompt = true,
  showSelect = false,
  isBusy = false,
}) => {
  const t = useTranslate();
  const { Paragraph } = Typography;
  const { theme } = useAntTheme();

  const isSelected = script.selected === true;
  // Selection is sticky: clicking the chosen version is a no-op. To switch,
  // the user picks a different version, which deselects the others.
  const selectScript = () => {
    if (isSelected) return;
    changeScript(index, {
      ...script,
      selected: true,
    });
  };

  const [count, setCount] = useState<number | null>(null);
  const { pause: pauseAudio, stop: stopAudio } = useTextToSpeech({ sectionId });

  const countValue = count ?? countScriptWords(script.content ?? "");

  const handleTabChange = (activeKey: string) => {
    const key = Number.parseInt(activeKey);
    const revision = script.revisions?.[key];
    if (!revision) return;
    const count = countScriptWords(revision.content);
    setCount(count);
  };

  const hasHighlights =
    splitTextWithHighlightTags(script.content ?? "", "HL").length > 0;

  const hasRevisions = (script.revisions?.length ?? 0) > 0;

  // Renders script text with optional editing
  const renderScriptText = (content: string, isEditable: boolean) => {
    if (hasHighlights) {
      return (
        <ScriptWithHighlights
          script={content}
          index={index}
          onEditStart={() => {
            pauseAudio().catch((error) => {
              console.error("Error pausing audio", error);
            });
          }}
          onEditComplete={
            isEditable
              ? (id, updatedScript) => {
                  changeScript(id, updatedScript);
                  stopAudio();
                }
              : undefined
          }
        />
      );
    }

    return (
      <Paragraph
        style={{
          width: "100%",
          margin: 5,
          marginBottom: 20,
          boxSizing: "border-box",
        }}
        editable={
          isEditable
            ? {
                icon: <EditOutlined style={{ color: theme.colorPrimary }} />,
                triggerType: ["text", "icon"],
                onChange: (value) => {
                  // Only block a brand-new (still empty) chapter script so its
                  // warning and "Generate chapter" button stay. Allow emptying
                  // a script that already has content.
                  if (!value.trim() && !script.content?.trim()) return;
                  changeScript(index, { content: value });
                  stopAudio();
                },
                onStart: () => {
                  pauseAudio().catch((error) => {
                    console.error("Error pausing audio", error);
                  });
                },
              }
            : undefined
        }
      >
        {content}
      </Paragraph>
    );
  };

  // Renders the main script content (either simple text or tabs with revisions)
  const renderScriptContent = () => {
    if (hasRevisions) {
      return (
        <Tabs
          size="small"
          onChange={handleTabChange}
          items={
            script.revisions?.map((revision, revisionIndex) => {
              const isOriginal = revision.prompt.length === 0;
              const isLatest = revisionIndex === 0;
              const content = isLatest ? script.content : revision.content;

              return {
                key: String(revisionIndex),
                label: (
                  <Space align="center">
                    {isOriginal ? (
                      <>
                        <TextTIcon size={16} />
                        {t("storyboard.editor.Script.original")}
                      </>
                    ) : (
                      revision.prompt
                    )}
                  </Space>
                ),
                children: renderScriptText(content ?? "", isLatest),
              };
            }) ?? []
          }
        />
      );
    }

    return renderScriptText(script.content ?? "", true);
  };

  return (
    <Card
      title={
        <Label
          type="secondary"
          style={{ color: isSelected ? "#43C0A0" : undefined }}
        >
          #{index + 1} •
          {t("storyboard.editor.Script.words", {
            count: countValue,
          })}
        </Label>
      }
      size="small"
      style={{
        borderRadius: 0,
        background: "none",
        borderTop: 0,
        borderLeft: 0,
        borderRight: 0,
        borderBottom: 0,
        marginTop: index === 0 ? 5 : 0,
      }}
      styles={{
        header: { borderRadius: 0, border: 0 },
        body: { paddingTop: 0, paddingBottom: 0 },
      }}
      extra={
        showSelect
          ? [
              <Button
                key="check"
                size="small"
                shape="round"
                icon={
                  script.selected ? (
                    <CheckCircleOutlined
                      style={{ color: theme.colorPrimary }}
                    />
                  ) : (
                    <CheckOutlined />
                  )
                }
                type={script.selected ? "primary" : "default"}
                ghost={script.selected ?? false}
                style={script.selected ? { cursor: "default" } : undefined}
                onClick={selectScript}
              >
                {script.selected
                  ? t("storyboard.editor.Script.chosenVersion")
                  : t("storyboard.editor.Script.chooseVersion")}
              </Button>,
            ]
          : undefined
      }
    >
      <Flex align="flex-start" gap={10}>
        {script.content && (
          <PlayScript
            text={script.content}
            sectionId={sectionId}
            disabled={!script.content}
            size="small"
            style={{
              marginTop: 5,
            }}
          />
        )}
        <div style={{ flex: 1 }}>{renderScriptContent()}</div>
      </Flex>
      {showPrompt && (
        <PromptForm
          disabled={!editable}
          busy={isBusy}
          value=""
          onChange={(value) => {
            improveScript(sectionId, script, value, () => {
              return { sectionId, scriptIndex: index };
            });
            return value;
          }}
        />
      )}
    </Card>
  );
};

//  cf https://github.com/bcExpt1123/chatgpt-example/blob/main/frontend/src/components/ChatForm/index.js
const PromptForm = ({
  onChange,
  style = {},
  busy = false,
  disabled = false,
  value,
  storyboardId,
  sectionCount,
  currentSectionIndex,
}: {
  onChange: (prompt: string) => void;
  style?: CSSProperties;
  busy?: boolean;
  disabled?: boolean;
  value: string;
  storyboardId?: string;
  sectionCount?: number;
  currentSectionIndex?: number;
}) => {
  const t = useTranslate();
  const [form] = Form.useForm();
  const prompt = Form.useWatch("prompt", { form, preserve: true });
  const { trackEvent } = useAnalytics();
  const promptTemplates: MenuProps["items"] = [
    {
      key: t("storyboard.editor.Script.shorten"),
      label: (
        <Space align="center">
          <StackMinusIcon size={24} />
          {t("storyboard.editor.Script.shorten")}
        </Space>
      ),
    },
    {
      key: t("storyboard.editor.Script.elaborate"),
      label: (
        <Space align="center">
          <StackPlusIcon size={24} />
          {t("storyboard.editor.Script.elaborate")}
        </Space>
      ),
    },
  ];

  const handleFinish = (values: {
    prompt: string;
    sendMsg?: unknown;
    template?: unknown;
  }) => {
    if (busy) return;

    trackEvent(AnalyticsEvents.StoryboardPromptSend, {
      storyboard_id: storyboardId,
      storyboard_sections_count: sectionCount,
      storyboard_section_index: currentSectionIndex,
      prompt_length: values.prompt.length,
      target: "section",
    });

    onChange(values.prompt);
    form.setFieldValue("prompt", "");
  };

  const handleSubmit = () => {
    form.submit();
  };

  // Pick menu item
  const handlePickTemplate: MenuProps["onClick"] = (e) => {
    // not clean with i18n  ... todo
    onChange(e.key);
  };

  useEffect(() => {
    if (value) form.setFieldValue("prompt", value);
  }, [value, form]);

  //Prompt menu items
  const menuProps = {
    items: promptTemplates,
    onClick: handlePickTemplate,
  };

  const { theme } = useAntTheme();

  const handleInputFocus = () => {
    trackEvent(AnalyticsEvents.StoryboardPromptFocus, {
      storyboard_id: storyboardId,
      storyboard_sections_count: sectionCount,
      storyboard_section_index: currentSectionIndex,
      target: "section",
    });
  };

  return (
    <div style={style}>
      <Form
        // layout="inline"
        form={form}
        onFinish={handleFinish}
        style={{ position: "relative" }}
      >
        <Form.Item
          style={{ position: "absolute", left: 0, top: 0, zIndex: 1 }}
          name="template"
        >
          <Dropdown menu={menuProps}>
            <Button
              type="text"
              size="large"
              style={{ opacity: 8 }}
              icon={<MagicWandIcon size={20} />}
            ></Button>
          </Dropdown>
        </Form.Item>
        <ConfigProvider
          theme={{ components: { Input: { colorBorder: "transparent" } } }}
        >
          <Form.Item name="prompt" initialValue={value}>
            <Input
              size="large"
              disabled={disabled}
              placeholder={t("storyboard.editor.Script.askAiTo")}
              style={{
                height: 40,
                backgroundColor: theme.colorBgContainerDisabled,
                paddingRight: 60,
                paddingLeft: 40,
              }}
              onFocus={handleInputFocus}
            />
          </Form.Item>
        </ConfigProvider>
        <Form.Item
          name="sendMsg"
          style={{ position: "absolute", right: 14, top: 4, zIndex: 1 }}
        >
          <Button
            onClick={handleSubmit}
            type="text"
            size="small"
            icon={<ArrowUpOutlined />}
            style={{
              height: 30,
              width: 30,
              borderRadius: 6,
              boxShadow: "none",
              border: 0,
              ...(busy ? { background: "transparent" } : {}),
            }}
            loading={busy}
            disabled={disabled || (!prompt && !busy)}
          ></Button>
        </Form.Item>
      </Form>
    </div>
  );
};
